/**
 * Roles and permissions.
 *
 * The rule that matters commercially: a cashier never sees what the books make of costs, the
 * margin, what a product costs on average, what the stock is worth (`ver_costos`). Everything
 * else is convenience; that one is why the owner can leave someone alone at the counter. Since
 * a cashier records the purchases (the owner, 2026-10-09), what an invoice says a purchase cost
 * is in the hands of whoever types it in, as the invoice itself already was.
 */

export const ROLES = ['dueno', 'cajero', 'repartidor'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  dueno: 'Dueño',
  cajero: 'Cajero',
  repartidor: 'Repartidor',
};

export const PERMISSIONS = [
  'vender',
  'anular_venta',
  'devolver',
  'precio_override',
  /**
   * Selling below the margin floor, and a ticket's quick sales past «Venta rápida sin el dueño»:
   * prices nothing can vouch for, so both are the owner's to say yes to.
   */
  'precio_bajo_minimo',
  /**
   * Setting a client's price list: what every sale to them is charged at from then on, with nobody
   * choosing it at the till. The owner's.
   */
  'elegir_lista',
  /**
   * Charging a sale, or a line of it, at a price list other than its own (the order's, the
   * client's, the shop's), at the till. The prices on it match a list, so nothing else flags them:
   * whoever may pick the wholesale list could charge retail, ring wholesale and keep the difference.
   * A cashier's all the same since 2026-10-15 (the owner: «Cashier role shouldn't need owner
   * authorization to put the wholesale price of a product»); a driver still asks for the owner's PIN.
   */
  'cobrar_otra_lista',
  'ver_costos',
  'ver_reportes',
  'editar_productos',
  /**
   * What a product sells for: its prices on each list, a new product's included, and the lists
   * themselves, the default one too, which is what the till charges. The owner's (2026-10-13: «also
   * cashier's should not be able to edit a product's price in the products menu»): a cashier keeps
   * the products, their names, codes and presentations, and types a price for one sale at the till
   * (`precio_override`).
   */
  'editar_precios',
  'recibir_compras',
  /**
   * Changing what the shop knows of a supplier: the name, the phone, whom to ask for, the notes. The
   * owner's (2026-10-16: «the option to edit their information if you are the owner»), where adding
   * one is anybody's who records a purchase (`recibir_compras`).
   */
  'editar_proveedores',
  'ajustar_inventario',
  'reempacar',
  'abrir_caja',
  'cerrar_caja',
  'confirmar_transferencia',
  'ruta_propia',
  /**
   * Paying for something on route — fuel, a toll, a tyre — out of the route's own cash, recorded
   * as it happens so the settlement stops expecting it. Only from a truck's open till: every other
   * expense stays the owner's.
   */
  'gasto_de_ruta',
  'cargar_camion',
  'liquidar_ruta',
  'gestionar_clientes',
  /**
   * Deciding who may buy on credit, and how much. Selling within a limit is any cashier's job;
   * setting the limit, or going past it, is the owner's, because it is the owner's money that
   * walks out of the door unpaid.
   */
  'dar_credito',
  'gestionar_usuarios',
  /**
   * Money outside the drawer: receiving and counting what a till hands over, counting general
   * cash, and squaring the bank and the wallet with their statements. Whoever holds the safe.
   */
  'recibir_efectivo',
  'gestionar_planilla',
  'configurar',
  'ver_bitacora',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const CAJERO: readonly Permission[] = [
  'vender',
  'anular_venta',
  'devolver',
  'precio_override',
  // The wholesale price at the till without the owner's PIN (the owner, 2026-10-15). A client's own
  // list stays the owner's to set (`elegir_lista`).
  'cobrar_otra_lista',
  'abrir_caja',
  'cerrar_caja',
  'gestionar_clientes',
  // The goods as well as the counter (the owner, 2026-10-09: «on the cashier's view we also want
  // them to have access to "Bought Goods", "Inventory", and "Products"»): what came in and what it
  // cost on the invoice, the stock, counting it, moving it, repacking it, taking out what is
  // damaged, and the products. The costs the books work out stay the owner's, and since 2026-10-13
  // what the products sell for too (`editar_precios`).
  'editar_productos',
  'recibir_compras',
  'ajustar_inventario',
  'reempacar',
];

const REPARTIDOR: readonly Permission[] = [
  'vender',
  'abrir_caja',
  'cerrar_caja',
  'ruta_propia',
  'gasto_de_ruta',
  'gestionar_clientes',
];

export const ROLE_PERMISSIONS: Record<Role, ReadonlySet<Permission>> = {
  dueno: new Set(PERMISSIONS),
  cajero: new Set(CAJERO),
  repartidor: new Set(REPARTIDOR),
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

/**
 * Going below the configured margin floor is deliberately NOT a cashier permission, even
 * though overriding a price is. That split is the whole point of requirement 5: the cashier
 * can negotiate, the owner authorises a loss. Both land in the bitácora either way.
 */
export function requiresOwnerPin(role: Role, permission: Permission): boolean {
  return !can(role, permission) && ROLE_PERMISSIONS.dueno.has(permission);
}
